import { Clock, Sparkles } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
    GENERATION_PHASE_CEILING,
    GENERATION_PHASE_PROGRESS,
    GENERATION_PHASE_TAU_MS,
    getGenerationPhaseLabel,
} from '../../../lib/constants';
import type { Eta, GenerationPhase } from '../../../types/domain';

interface Props {
  songName: string;
  phase: GenerationPhase;
  eta: Eta | null;
}

const QUEUE_PHASES: GenerationPhase[] = ['queued', 'awaiting_generation'];
const VOICE_PREPARATION_PHASE: GenerationPhase = 'preparing_voice';

const MINUTE_MS = 60_000;

const ISO_8601_DURATION_RE =
  /^P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)W)?(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?)?$/;

function parseIsoDurationMs(iso: string): number {
  const match = ISO_8601_DURATION_RE.exec(iso);
  if (!match) return 0;

  const [, years, months, weeks, days, hours, minutes, seconds] = match;
  const DAY_MS = 24 * 60 * 60 * 1000;

  return (
    Number(years || 0) * 365 * DAY_MS +
    Number(months || 0) * 30 * DAY_MS +
    Number(weeks || 0) * 7 * DAY_MS +
    Number(days || 0) * DAY_MS +
    Number(hours || 0) * 60 * MINUTE_MS +
    Number(minutes || 0) * MINUTE_MS +
    Number(seconds || 0) * 1_000
  );
}

type EtaTier = 'withinP50' | 'withinP90' | 'overP90';

function useEtaAnchor(eta: Eta | null): number | null {
  const anchorRef = useRef<{ key: string; timestamp: number } | null>(null);

  if (!eta) {
    anchorRef.current = null;
  } else {
    const key = `${eta.p50}:${eta.p90}`;
    if (anchorRef.current?.key !== key) {
      anchorRef.current = { key, timestamp: Date.now() };
    }
  }

  return anchorRef.current?.timestamp ?? null;
}

function useEtaTier(
  eta: Eta | null,
  anchor: number | null,
  active: boolean,
): EtaTier {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!active || !eta) return;
    const id = setInterval(() => setNow(Date.now()), 20_000);
    return () => clearInterval(id);
  }, [active, eta]);

  return useMemo(() => {
    if (!eta || anchor === null) return 'withinP50';
    const targetP50 = anchor + parseIsoDurationMs(eta.p50);
    const targetP90 = anchor + parseIsoDurationMs(eta.p90);
    if (now < targetP50) return 'withinP50';
    if (now < targetP90) return 'withinP90';
    return 'overP90';
  }, [eta, anchor, now]);
}

function formatEtaRange(eta: Eta, anchor: number): string {
  const now = Date.now();
  const minutesUntil = (iso: string) =>
    Math.max(
      1,
      Math.round((anchor + parseIsoDurationMs(iso) - now) / MINUTE_MS),
    );

  const p50Min = minutesUntil(eta.p50);
  const p90Min = minutesUntil(eta.p90);

  return p90Min <= p50Min ? `около ${p50Min} мин` : `${p50Min}–${p90Min} мин`;
}

function getWaitingCopy(
  tier: EtaTier,
  eta: Eta | null,
  anchor: number | null,
): string {
  switch (tier) {
    case 'withinP50':
      return eta && anchor !== null
        ? `Обычно генерация начинается в течение ${formatEtaRange(eta, anchor)}`
        : 'Песня в очереди — скоро начнём';
    case 'withinP90':
      return 'Уже почти ваша очередь — осталось совсем немного';
    case 'overP90':
      return 'Сейчас нагрузка выше обычной. Мы точно запустим генерацию — это займёт чуть больше времени, чем обычно';
  }
}

const PROGRESS_TICK_MS = 400;

function useLiveProgress(phase: GenerationPhase): number {
  const floor = GENERATION_PHASE_PROGRESS[phase] ?? 10;
  const ceiling = GENERATION_PHASE_CEILING[phase] ?? Math.min(99, floor + 15);
  const tau = GENERATION_PHASE_TAU_MS[phase] ?? 15_000;

  const anchorRef = useRef<{ phase: GenerationPhase; timestamp: number }>({
    phase,
    timestamp: Date.now(),
  });
  if (anchorRef.current.phase !== phase) {
    anchorRef.current = { phase, timestamp: Date.now() };
  }

  const [, forceTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => forceTick((n) => n + 1), PROGRESS_TICK_MS);
    return () => clearInterval(id);
  }, [phase]);

  const elapsed = Date.now() - anchorRef.current.timestamp;
  const eased = ceiling - (ceiling - floor) * Math.exp(-elapsed / tau);
  return Math.min(ceiling, Math.max(floor, Math.round(eased)));
}

export function StepGenerating({ songName, phase, eta }: Props) {
  const isPreparingVoice = phase === VOICE_PREPARATION_PHASE;
  const isWaiting = QUEUE_PHASES.includes(phase);
  // Название может быть неизвестно (например, после перезагрузки страницы)
  const forSong = songName ? <> для «{songName}»</> : null;
  const progress = useLiveProgress(phase);
  const statusLabel = getGenerationPhaseLabel(phase);

  const anchor = useEtaAnchor(eta);
  const tier = useEtaTier(eta, anchor, isWaiting);
  const waitingCopy = isWaiting ? getWaitingCopy(tier, eta, anchor) : null;

  return (
    <motion.div
      key="generating"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className="max-w-2xl w-full text-center"
    >
      <div className="bg-white/80 backdrop-blur-md rounded-3xl p-6 sm:p-12 shadow-xl">
        <AnimatePresence mode="wait">
          <motion.div
            key={
              isPreparingVoice
                ? 'voice-preparation-icon'
                : isWaiting
                  ? 'waiting-icon'
                  : 'working-icon'
            }
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.85 }}
            transition={{ duration: 0.25 }}
          >
            {isWaiting || isPreparingVoice ? (
              <Clock className="w-20 h-20 mx-auto mb-6 text-purple-600" />
            ) : (
              <Sparkles className="w-20 h-20 mx-auto mb-6 text-purple-600 animate-spin" />
            )}
          </motion.div>
        </AnimatePresence>

        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-4 text-gray-900">
          {isPreparingVoice ? (
            <>Подготавливаем голос{forSong}...</>
          ) : isWaiting ? (
            <>Песня{songName ? <> «{songName}»</> : null} поставлена в очередь</>
          ) : (
            <>Нейросеть сочиняет трек{forSong}...</>
          )}
        </h2>

        <div
          className="w-full bg-gray-200 rounded-full h-4 mb-4 overflow-hidden"
          role="progressbar"
          aria-valuenow={progress}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={
            isPreparingVoice ? 'Прогресс подготовки голоса' : 'Прогресс генерации'
          }
        >
          <motion.div
            className="h-full bg-gradient-to-r from-purple-600 to-pink-600"
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          />
        </div>

        <p className="text-lg text-purple-600 font-medium" aria-live="polite">
          {statusLabel}
        </p>
        <p className="text-sm text-gray-500 mt-2">{progress}%</p>

        {isPreparingVoice ? (
          <div className="mt-6 rounded-xl bg-purple-50 border border-purple-200 p-4">
            <p className="text-sm font-medium text-purple-700" aria-live="polite">
              Это может занять немного времени. После подготовки голоса мы сразу
              запустим генерацию песни.
            </p>
          </div>
        ) : isWaiting && waitingCopy ? (
          <AnimatePresence mode="wait">
            <motion.div
              key={tier}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.25 }}
              className={`mt-6 rounded-xl border p-4 ${
                tier === 'overP90'
                  ? 'bg-amber-50 border-amber-200'
                  : 'bg-purple-50 border-purple-200'
              }`}
            >
              <p
                className={`text-sm font-medium ${
                  tier === 'overP90' ? 'text-amber-800' : 'text-purple-700'
                }`}
                aria-live="polite"
              >
                {waitingCopy}
              </p>
            </motion.div>
          </AnimatePresence>
        ) : null}

        <div className="mt-6 rounded-xl bg-gray-50 border border-gray-200 p-4">
          <p className="text-sm text-gray-600">
            Создание трека может занять несколько минут. Пожалуйста, дождитесь
            завершения генерации и не закрывайте страницу — а если случайно
            закроете, просто откройте её снова: мы продолжим с того же места.
          </p>
        </div>
      </div>
    </motion.div>
  );
}
